import { z } from 'zod';
import {
  InvoiceSchema,
  isInvoiceEditable,
  type Invoice,
  type OwnerInvoice,
} from '../../domain/entities/invoice.ts';
import type { CreditMemo, Deposit } from '../../domain/entities/credit.ts';
import { TaxRateSchema, type TaxRate } from '../../domain/entities/tax-rate.ts';
import { CostLineSchema, type CostLine } from '../../domain/entities/cost-line.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { FunctionsPort } from '../../ports/functions-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Owner invoices, and the company's tax rates (P5-12a).
 *
 * Writing an unreleased invoice is a client write: somebody builds one up
 * over a morning and a round trip per line would be a worse product for no
 * more safety. **Releasing it is a callable**, because that is the moment
 * the owner is asked for money — and so is recording a payment, for the
 * same reason a bill's is.
 */
export class InvoiceError extends Error {
  readonly reason: 'invalid' | 'notEditable';

  constructor(reason: 'invalid' | 'notEditable', message: string) {
    super(message);
    this.name = 'InvoiceError';
    this.reason = reason;
  }
}

export const SaveInvoiceInputSchema = z.object({
  title: z.string().trim().min(1, 'An invoice needs a title').max(200),
  description: z.string().max(20_000).optional(),
  deadline: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  lines: z.array(CostLineSchema).optional(),
  /**
   * The chosen rate, as an id **and** its rate at this moment.
   *
   * Both, because the id is what an accounting sync maps and the frozen
   * rate is what was charged. Passing the id alone would make an invoice's
   * tax change when somebody edits the rate next year.
   */
  taxRate: z
    .object({ id: z.string().min(1), rateMillionths: z.number().int().min(0).max(1_000_000) })
    .nullable()
    .optional(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type SaveInvoiceInput = z.input<typeof SaveInvoiceInputSchema>;

export interface InvoiceContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

type ReadContext = Pick<InvoiceContext, 'data' | 'companyId' | 'jobId'>;

function repo(context: ReadContext) {
  return context.data.collection<Invoice>(
    { companyId: context.companyId, jobId: context.jobId },
    'invoices',
  );
}

/** A job's invoices, highest number first. */
export async function listInvoices(context: ReadContext): Promise<Invoice[]> {
  const page = await repo(context).list({ orderBy: [{ field: 'number', direction: 'desc' }] });
  return [...page.items];
}

export function subscribeToInvoices(
  context: ReadContext,
  onChange: (invoices: readonly Invoice[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'number', direction: 'desc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function saveInvoice(
  context: InvoiceContext,
  id: string,
  input: SaveInvoiceInput,
): Promise<Invoice> {
  const parsed = SaveInvoiceInputSchema.safeParse(input);
  if (!parsed.success) throw new InvoiceError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('invoices', id);
  if (!isInvoiceEditable(existing.status)) {
    throw new InvoiceError(
      'notEditable',
      existing.status === 'void'
        ? 'A voided invoice cannot be changed.'
        : 'This invoice has been released to the owner. Pull it back to change it.',
    );
  }

  const patch = {
    title: parsed.data.title,
    description: parsed.data.description ?? existing.description,
    lines: parsed.data.lines ?? existing.lines,
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    ...(parsed.data.deadline === null
      ? { deadline: undefined }
      : parsed.data.deadline !== undefined
        ? { deadline: parsed.data.deadline }
        : {}),
    ...(parsed.data.taxRate === null
      ? { taxRateId: undefined, taxRateMillionths: 0 }
      : parsed.data.taxRate !== undefined
        ? {
            taxRateId: parsed.data.taxRate.id,
            taxRateMillionths: parsed.data.taxRate.rateMillionths,
          }
        : {}),
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = InvoiceSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new InvoiceError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'invoices' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

export function withInvoiceLineReplaced(
  invoice: Pick<Invoice, 'lines'>,
  line: CostLine,
): CostLine[] {
  const existing = invoice.lines.findIndex((candidate) => candidate.id === line.id);
  if (existing === -1) return [...invoice.lines, line];
  return invoice.lines.map((candidate) => (candidate.id === line.id ? line : candidate));
}

export function withInvoiceLineRemoved(
  invoice: Pick<Invoice, 'lines'>,
  lineId: string,
): CostLine[] {
  return invoice.lines.filter((line) => line.id !== lineId);
}

// ---- tax rates, a company record ----------------------------------------

function taxRepo(context: Pick<InvoiceContext, 'data' | 'companyId'>) {
  return context.data.collection<TaxRate>({ companyId: context.companyId }, 'taxRates');
}

export async function listTaxRates(
  context: Pick<InvoiceContext, 'data' | 'companyId'>,
): Promise<TaxRate[]> {
  const page = await taxRepo(context).list({ orderBy: [{ field: 'name', direction: 'asc' }] });
  return [...page.items];
}

export function subscribeToTaxRates(
  context: Pick<InvoiceContext, 'data' | 'companyId'>,
  onChange: (rates: readonly TaxRate[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return taxRepo(context).subscribe(
    { orderBy: [{ field: 'name', direction: 'asc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function saveTaxRate(
  context: Omit<InvoiceContext, 'jobId'>,
  input: { readonly id?: string; readonly name: string; readonly rateMillionths: number },
): Promise<TaxRate> {
  const now = context.clock.now().toISOString();
  const id = input.id ?? newId();
  const existing = await taxRepo(context).get(id);

  const checked = TaxRateSchema.safeParse({
    ...(existing ?? {}),
    id,
    companyId: context.companyId,
    name: input.name,
    rateMillionths: input.rateMillionths,
    active: existing?.active ?? true,
    createdAt: existing?.createdAt ?? now,
    createdBy: existing?.createdBy ?? context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  });
  if (!checked.success) throw new InvoiceError('invalid', checked.error.issues[0]!.message);

  if (existing) await taxRepo(context).update(id, checked.data);
  else await taxRepo(context).create(checked.data);
  return checked.data;
}

/**
 * Retire a rate rather than delete it.
 *
 * The same reasoning as a cost code: an invoice issued two years ago points
 * at the rate it used, and deleting the rate would turn that invoice's tax
 * into a figure with no explanation. There is no delete here at all, and
 * the rules refuse one.
 */
export async function setTaxRateActive(
  context: Omit<InvoiceContext, 'jobId'>,
  id: string,
  active: boolean,
): Promise<TaxRate> {
  const existing = await taxRepo(context).get(id);
  if (!existing) throw new NotFoundError('taxRates', id);
  const patch = { active, updatedAt: context.clock.now().toISOString(), updatedBy: context.uid };
  await taxRepo(context).update(id, patch);
  return { ...existing, ...patch };
}

// ---- the callables, wrapped ---------------------------------------------

export async function createInvoice(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly title: string;
    readonly invoiceId?: string;
  },
): Promise<{ readonly invoiceId: string; readonly number: string }> {
  const invoiceId = input.invoiceId ?? newId();
  return functions.call('createInvoice', {
    companyId: input.companyId,
    jobId: input.jobId,
    invoiceId,
    title: input.title,
  });
}

export async function transitionInvoice(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly invoiceId: string;
    readonly to: 'released' | 'unreleased' | 'void';
  },
): Promise<{ readonly status: string }> {
  return functions.call('transitionInvoice', input);
}

export async function recordInvoicePayment(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly invoiceId: string;
    readonly amountMinor: number;
    readonly method: 'check' | 'card' | 'ach' | 'cash' | 'other';
    readonly reference?: string;
    readonly paymentId?: string;
  },
): Promise<{ readonly paymentId: string; readonly paidMinor: number }> {
  const paymentId = input.paymentId ?? newId();
  return functions.call('recordInvoicePayment', {
    companyId: input.companyId,
    jobId: input.jobId,
    invoiceId: input.invoiceId,
    paymentId,
    amountMinor: input.amountMinor,
    method: input.method,
    ...(input.reference === undefined ? {} : { reference: input.reference }),
  });
}

// ---- the owner's own copies (P5-12c) ------------------------------------

/**
 * The owner's invoices, from the mirror.
 *
 * Read-only and write-free: a trigger derives these, and a client that
 * could write one could change what it is being asked to pay. The one
 * thing an owner may change is the record of having looked, and that goes
 * through `markInvoiceViewed` so the office's own document is what gets
 * stamped.
 */
export function subscribeToOwnerInvoices(
  context: ReadContext,
  onChange: (invoices: readonly OwnerInvoice[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return context.data
    .collection<OwnerInvoice>(
      { companyId: context.companyId, jobId: context.jobId },
      'ownerInvoices',
    )
    .subscribe({}, (page) => onChange(page.items), onError);
}

export async function listOwnerInvoices(context: ReadContext): Promise<OwnerInvoice[]> {
  const page = await context.data
    .collection<OwnerInvoice>(
      { companyId: context.companyId, jobId: context.jobId },
      'ownerInvoices',
    )
    .list();
  return [...page.items];
}

/**
 * Record that the owner has opened an invoice.
 *
 * A callable because it writes the **office's** document — the owner reads
 * a mirror, and the office is the one who wants to know whether their
 * customer has looked. Idempotent on purpose: the first view is the
 * interesting fact, and re-stamping it every time they open the page would
 * turn "seen on the 3rd" into "seen just now", which answers a question
 * nobody asked.
 */
export async function markInvoiceViewed(
  functions: FunctionsPort,
  input: { readonly companyId: string; readonly jobId: string; readonly invoiceId: string },
): Promise<{ readonly viewedAt: string }> {
  return functions.call('markInvoiceViewed', input);
}

// ---- credit memos and deposits (P5-13) ----------------------------------

/**
 * A job's credit memos and deposits.
 *
 * Read-only from a client. Every write goes through a callable, because
 * each of them changes what a customer owes — see `createCreditMemo` and
 * the four beside it.
 */
export function subscribeToCreditMemos(
  context: ReadContext,
  onChange: (credits: readonly CreditMemo[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return context.data
    .collection<CreditMemo>({ companyId: context.companyId, jobId: context.jobId }, 'creditMemos')
    .subscribe(
      { orderBy: [{ field: 'number', direction: 'desc' }] },
      (page) => onChange(page.items),
      onError,
    );
}

export function subscribeToDeposits(
  context: ReadContext,
  onChange: (deposits: readonly Deposit[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return context.data
    .collection<Deposit>({ companyId: context.companyId, jobId: context.jobId }, 'deposits')
    .subscribe(
      { orderBy: [{ field: 'number', direction: 'desc' }] },
      (page) => onChange(page.items),
      onError,
    );
}

export async function createCreditMemo(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly amountMinor: number;
    readonly reason: string;
    readonly creditMemoId?: string;
  },
): Promise<{ readonly creditMemoId: string; readonly number: string }> {
  const creditMemoId = input.creditMemoId ?? newId();
  return functions.call('createCreditMemo', {
    companyId: input.companyId,
    jobId: input.jobId,
    creditMemoId,
    amountMinor: input.amountMinor,
    reason: input.reason,
  });
}

export async function applyCreditMemo(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly creditMemoId: string;
    readonly invoiceId: string;
  },
): Promise<{ readonly invoiceId: string }> {
  return functions.call('applyCreditMemo', input);
}

export async function voidCreditMemo(
  functions: FunctionsPort,
  input: { readonly companyId: string; readonly jobId: string; readonly creditMemoId: string },
): Promise<{ readonly creditMemoId: string }> {
  return functions.call('voidCreditMemo', input);
}

export async function recordDeposit(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly amountMinor: number;
    readonly receivedAt: string;
    readonly method: 'check' | 'card' | 'ach' | 'cash' | 'other';
    readonly reference?: string;
    readonly depositId?: string;
  },
): Promise<{ readonly depositId: string; readonly number: string }> {
  const depositId = input.depositId ?? newId();
  return functions.call('recordDeposit', {
    companyId: input.companyId,
    jobId: input.jobId,
    depositId,
    amountMinor: input.amountMinor,
    receivedAt: input.receivedAt,
    method: input.method,
    ...(input.reference === undefined ? {} : { reference: input.reference }),
  });
}

export async function applyDeposit(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly depositId: string;
    readonly invoiceId: string;
    readonly amountMinor: number;
  },
): Promise<{ readonly remainingMinor: number }> {
  return functions.call('applyDeposit', input);
}
